import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Pencil } from 'lucide-react';
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import type { LightGallery } from 'lightgallery/lightgallery';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-thumbnail.css';
import { Photo, Overlay, OverlayConfig } from '../../types';
import { useEditorStore } from '../../store/editorStore';
import styles from './PhotoViewerModal.module.css';

interface PhotoViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  photos: Photo[];
  initialIndex: number;
  photographerById?: Record<string, string>;
  overlays?: Overlay[];
  /** Reflects an editor-driven highlight change back into the gallery. */
  onHighlightChange?: (id: string, isHighlight: boolean) => void;
  defaultOverlayConfig?: OverlayConfig | null;
}

// Thumbnail strip height (matches thumbHeight option below)
const THUMB_HEIGHT = 72;

export const PhotoViewerModal: React.FC<PhotoViewerModalProps> = ({
  isOpen,
  onClose,
  photos,
  initialIndex,
  photographerById = {},
  overlays = [],
  onHighlightChange,
  defaultOverlayConfig,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lgRef = useRef<LightGallery | null>(null);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const currentIndexRef = useRef(initialIndex);
  const [lgIsOpen, setLgIsOpen] = useState(false);
  const { openEditor } = useEditorStore();

  // Stable refs so the init effect never needs to re-run
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;
  const onHighlightChangeRef = useRef(onHighlightChange);
  onHighlightChangeRef.current = onHighlightChange;
  const defaultOverlayConfigRef = useRef(defaultOverlayConfig);
  defaultOverlayConfigRef.current = defaultOverlayConfig;
  const photosRef = useRef(photos);
  photosRef.current = photos;

  const slides = useMemo(
    () =>
      photos.map((p) => ({
        src: p.processedUrl ?? p.originalUrl ?? '',
        thumb: p.processedUrl ?? p.thumbnailUrl ?? p.originalUrl ?? '',
      })),
    [photos],
  );

  // Initialise / destroy lightgallery when the modal opens / closes.
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const container = containerRef.current;

    const handleAfterSlide = (e: Event) => {
      const idx = (e as CustomEvent).detail.index as number;
      setCurrentIndex(idx);
      currentIndexRef.current = idx;
    };
    const handleAfterOpen = () => {
      document.body.style.overflow = 'hidden';
      setLgIsOpen(true);
    };
    const handleBeforeClose = () => {
      setLgIsOpen(false);
    };
    const handleAfterClose = () => {
      document.body.style.overflow = '';
      onCloseRef.current();
    };

    container.addEventListener('lgAfterSlide', handleAfterSlide);
    container.addEventListener('lgAfterOpen', handleAfterOpen);
    container.addEventListener('lgBeforeClose', handleBeforeClose);
    container.addEventListener('lgAfterClose', handleAfterClose);

    lgRef.current = lightGallery(container, {
      dynamic: true,
      dynamicEl: slides,
      plugins: [lgThumbnail],
      speed: 300,
      preload: 4,
      download: false,
      escKey: true,
      keyPress: true,
      closeOnTap: true,
      swipeToClose: false,
      licenseKey: '0000-0000-000-0000',
      // Thumbnail strip settings
      thumbnail: true,
      animateThumb: true,
      toggleThumb: false,
      enableThumbDrag: true,
      enableThumbSwipe: true,
      thumbWidth: 80,
      thumbHeight: `${THUMB_HEIGHT}px`,
    });

    const openTimer = setTimeout(() => lgRef.current?.openGallery(initialIndex), 50);

    return () => {
      clearTimeout(openTimer);
      container.removeEventListener('lgAfterSlide', handleAfterSlide);
      container.removeEventListener('lgAfterOpen', handleAfterOpen);
      container.removeEventListener('lgBeforeClose', handleBeforeClose);
      container.removeEventListener('lgAfterClose', handleAfterClose);
      lgRef.current?.destroy();
      lgRef.current = null;
      document.body.style.overflow = '';
      // When the viewer is closed programmatically (e.g. opening the editor),
      // lgBeforeClose never fires, so we must clear lgIsOpen here to unmount
      // the bottom-overlay portal.
      setLgIsOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Sync index when modal is reopened at a different photo
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      currentIndexRef.current = initialIndex;
    }
  }, [isOpen, initialIndex]);

  const handleEdit = useCallback(() => {
    const photo = photosRef.current[currentIndexRef.current];
    if (photo) openEditor(photo, overlaysRef.current, onHighlightChangeRef.current, defaultOverlayConfigRef.current);
    onCloseRef.current();
  }, [openEditor]);

  const photo = photos[currentIndex];

  const photographer = useMemo(() => {
    if (!photo) return '';
    if (!photo.ftpAccountId) return 'Manual Upload';
    return photographerById[photo.ftpAccountId] || '';
  }, [photo, photographerById]);

  const dateLabel = photo ? new Date(photo.uploadedAt).toLocaleString() : '';

  const bottomBar = useMemo(() => {
    if (!photo) return null;
    return (
      <motion.div
        className={styles['bottom-overlay']}
        style={{ bottom: `${THUMB_HEIGHT}px` }}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className={styles['bottom-content']}>
          <div className={styles['info']}>
            <div className={styles['info-name']}>{photographer || 'Unknown photographer'}</div>
            <div className={styles['info-date']}>Uploaded: {dateLabel}</div>
          </div>
          <div className={styles['actions']}>
            <button onClick={handleEdit} className={styles['edit-button']}>
              <Pencil className={styles['icon-sm']} />
              Edit Photo
            </button>
          </div>
        </div>
      </motion.div>
    );
  }, [photo, photographer, dateLabel, handleEdit]);

  return (
    <>
      {/* Hidden mount point — lightgallery attaches its overlay to document.body */}
      <div ref={containerRef} style={{ display: 'none' }} />

      {lgIsOpen && bottomBar && createPortal(bottomBar, document.body)}
    </>
  );
};
